/**
 * GA4 Ecommerce - Wishlist Tracking
 * Tracks when products are added to wishlist
 */
(function() {
  'use strict';

  if (typeof dataLayer === 'undefined') {
    return;
  }

  // Track recently fired events to prevent duplicates (itemId -> timestamp)
  const trackedEvents = new Map();

  /**
   * Get product data from product page context
   */
  function getProductPageData() {
    const productSelector = document.querySelector('main-product-selector');
    if (!productSelector || !productSelector.ga4ProductData) {
      return null;
    }

    const form = document.querySelector('form[id^="product-form-"]');
    if (!form) {
      return null;
    }

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput) {
      return null;
    }

    const variantId = parseInt(variantIdInput.value);
    const variant = productSelector.ga4ProductData.variants.find(v => v.id === variantId);

    if (!variant) {
      return null;
    }

    return {
      productId: productSelector.ga4ProductData.product_id,
      productTitle: productSelector.ga4ProductData.product_title,
      productVendor: productSelector.ga4ProductData.product_vendor,
      productType: productSelector.ga4ProductData.product_type,
      currencyCode: productSelector.ga4ProductData.currency_code,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price
    };
  }

  /**
   * Get product data from product card context
   */
  function getProductCardData(button) {
    const cardProduct = button.closest('card-product');
    if (!cardProduct) {
      return null;
    }

    const productId = cardProduct.dataset.ga4ProductId;
    const variantId = button.dataset.ga4VariantId;
    const variantTitle = button.dataset.ga4VariantTitle;
    const price = parseFloat(button.dataset.ga4Price);

    if (!productId || !variantId) {
      return null;
    }

    return {
      productId: productId,
      productTitle: cardProduct.dataset.ga4ProductTitle,
      productVendor: cardProduct.dataset.ga4ProductVendor,
      productType: cardProduct.dataset.ga4ProductType,
      currencyCode: cardProduct.dataset.ga4Currency,
      variantId: variantId,
      variantTitle: variantTitle || 'Default Title',
      price: price
    };
  }

  /**
   * Push GA4 add_to_wishlist event
   */
  function pushWishlistEvent(productData) {
    if (!productData) {
      return;
    }

    const itemId = `shopify_${productData.productId}_${productData.variantId}`;

    // Prevent duplicate events within 2 seconds
    const now = Date.now();
    const lastTracked = trackedEvents.get(itemId);

    if (lastTracked && (now - lastTracked) < 2000) {
      return; // Skip duplicate event
    }

    trackedEvents.set(itemId, now);

    // Clean up old entries periodically
    if (trackedEvents.size > 100) {
      const twoSecondsAgo = now - 2000;
      for (const [key, timestamp] of trackedEvents.entries()) {
        if (timestamp < twoSecondsAgo) {
          trackedEvents.delete(key);
        }
      }
    }

    const itemName = productData.variantTitle && productData.variantTitle !== 'Default Title'
      ? `${productData.productTitle} - ${productData.variantTitle}`
      : productData.productTitle;

    const ga4Event = {
      'event': 'add_to_wishlist',
      'ecommerce': {
        'currency': productData.currencyCode,
        'value': productData.price,
        'items': [{
          'item_name': itemName,
          'item_id': itemId,
          'item_brand': productData.productVendor,
          'item_category': productData.productType,
          'currency': productData.currencyCode,
          'price': productData.price,
          'quantity': 1
        }]
      }
    };

    dataLayer.push({ 'ecommerce': null });  // Clear the previous ecommerce object

    dataLayer.push(ga4Event);
  }

  /**
   * Handle wishlist button class change
   */
  function handleWishlistChange(mutations) {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const button = mutation.target;

        // Only track when st-is-added is added (not removed)
        if (button.classList.contains('st-is-added') &&
            !mutation.oldValue?.includes('st-is-added')) {

          // Check if this is a product page or product card
          const isProductPage = button.dataset.ga4IsProductPage === 'true';

          let productData = null;
          if (isProductPage) {
            productData = getProductPageData();
          } else {
            productData = getProductCardData(button);
          }

          if (productData) {
            pushWishlistEvent(productData);
          }
        }
      }
    });
  }

  /**
   * Track wishlist button click (as backup if MutationObserver misses it)
   */
  function handleWishlistClick(event) {
    const button = event.currentTarget;

    // Use a small delay to allow the class to be added by the third-party app
    setTimeout(() => {
      if (button.classList.contains('st-is-added')) {
        const isProductPage = button.dataset.ga4IsProductPage === 'true';

        let productData = null;
        if (isProductPage) {
          productData = getProductPageData();
        } else {
          productData = getProductCardData(button);
        }

        if (productData) {
          pushWishlistEvent(productData);
        }
      }
    }, 300);
  }

  /**
   * Initialize wishlist tracking
   */
  function initWishlistTracking() {
    // Find all wishlist buttons
    const wishlistButtons = document.querySelectorAll('.st-wishlist-button');

    if (wishlistButtons.length === 0) {
      return;
    }

    // Create MutationObserver for each button
    wishlistButtons.forEach((button) => {
      const observer = new MutationObserver(handleWishlistChange);

      observer.observe(button, {
        attributes: true,
        attributeOldValue: true,
        attributeFilter: ['class']
      });

      // Also listen for clicks as backup
      button.addEventListener('click', handleWishlistClick);
    });

    // Also listen for dynamically added buttons (e.g., AJAX-loaded content)
    const dynamicObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the added node is a wishlist button
            if (node.classList && node.classList.contains('st-wishlist-button')) {
              const observer = new MutationObserver(handleWishlistChange);
              observer.observe(node, {
                attributes: true,
                attributeOldValue: true,
                attributeFilter: ['class']
              });
              node.addEventListener('click', handleWishlistClick);
            }
            // Check for wishlist buttons inside the added node
            const buttons = node.querySelectorAll && node.querySelectorAll('.st-wishlist-button');
            if (buttons) {
              buttons.forEach((button) => {
                const observer = new MutationObserver(handleWishlistChange);
                observer.observe(button, {
                  attributes: true,
                  attributeOldValue: true,
                  attributeFilter: ['class']
                });
                button.addEventListener('click', handleWishlistClick);
              });
            }
          }
        });
      });
    });

    // Observe the document body for dynamically added content
    dynamicObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlistTracking);
  } else {
    initWishlistTracking();
  }
})();
